import { NavLink } from "react-router-dom";
import { LayoutDashboard, Users, BellRing, CalendarDays, Search, HardDriveDownload, LogOut, ShieldCheck, UserCog } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { OPERATORI } from "@/lib/format";

const LOGO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_park-light-manager/artifacts/ljez3ua3_Senza%20titolo88.png";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: NAV.sidebarDashboard },
  { to: "/clienti", label: "Clienti", icon: Users, testid: NAV.sidebarClienti },
  { to: "/richiami", label: "Richiami", icon: BellRing, testid: NAV.sidebarRichiami },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, testid: NAV.sidebarCalendario },
];

const utils = [
  { to: "/ricerca-note", label: "Ricerca Note", icon: Search, testid: NAV.sidebarRicerca },
  { to: "/backup", label: "Backup", icon: HardDriveDownload, testid: NAV.sidebarBackup },
  { to: "/accessi", label: "Accessi", icon: ShieldCheck, testid: "nav-accessi" },
];

export default function Sidebar() {
  const { user, logout, scopedOperator, setScopedOperator } = useAuth();
  const canSwitch = Boolean(user?.can_switch_operator);
  const currentScope = scopedOperator || user?.operatore || "";
  return (
    <aside className="w-64 flex-shrink-0 hidden md:flex flex-col bg-[#1C1917] border-r border-stone-800 sidebar-scroll">
      <div className="px-5 py-6 border-b border-stone-800 flex flex-col items-center gap-2">
        <img
          src={LOGO_URL}
          alt="Irollo — Illumina & Arreda dal 1964"
          className="w-44 h-auto object-contain"
          style={{ filter: "invert(1) brightness(1.15) contrast(1.05)" }}
        />
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#F56A1F] font-semibold">
          Gestionale CRM
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto sidebar-scroll">
        <div className="px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          Navigazione
        </div>
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                isActive
                  ? "bg-[#F56A1F] text-white shadow-sm"
                  : "text-stone-400 hover:text-white hover:bg-stone-800/60"
              )
            }
          >
            <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
            <span>{label}</span>
          </NavLink>
        ))}

        <div className="px-3 pt-6 pb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          Strumenti
        </div>
        {utils.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                isActive
                  ? "bg-[#F56A1F] text-white shadow-sm"
                  : "text-stone-400 hover:text-white hover:bg-stone-800/60"
              )
            }
          >
            <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-stone-800 space-y-3">
        <div className="flex items-center gap-3 px-2 min-w-0">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-8 h-8 rounded-full object-cover shrink-0"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#F56A1F] text-white flex items-center justify-center text-xs font-semibold shrink-0">
              {(user?.name?.[0] || user?.email?.[0] || user?.username?.[0] || "I").toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-stone-500 uppercase tracking-widest">Sessione</div>
            <div className="text-sm text-white truncate">
              {user?.name || user?.email || user?.username || "—"}
            </div>
          </div>
        </div>

        {canSwitch ? (
          <div
            data-testid="sidebar-operator-switcher"
            className="mx-2 px-3 py-2 rounded-lg bg-[#F56A1F]/12 border border-[#F56A1F]/30 flex items-center gap-2"
          >
            <UserCog className="w-3.5 h-3.5 text-[#F56A1F] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-widest text-stone-400 font-semibold mb-0.5">
                Visualizza come
              </div>
              <Select value={currentScope} onValueChange={setScopedOperator}>
                <SelectTrigger
                  data-testid="sidebar-operator-select"
                  className="h-7 bg-stone-900/60 border-stone-700 text-[#F56A1F] font-semibold text-xs px-2 focus:ring-0 focus:ring-offset-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 text-stone-100 max-h-72">
                  {OPERATORI.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="focus:bg-[#F56A1F]/20 focus:text-white">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          user?.operatore && (
            <div
              data-testid="sidebar-operator-badge"
              className="mx-2 px-3 py-2 rounded-lg bg-[#F56A1F]/12 border border-[#F56A1F]/30 flex items-center gap-2"
            >
              <UserCog className="w-3.5 h-3.5 text-[#F56A1F] shrink-0" />
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-stone-400 font-semibold">Modalità</div>
                <div className="text-xs text-[#F56A1F] font-semibold truncate">{user.operatore}</div>
              </div>
            </div>
          )
        )}
        <button
          data-testid="sidebar-logout"
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-stone-400 hover:text-white hover:bg-stone-800/60 transition-colors">
          <LogOut className="w-4 h-4" strokeWidth={1.75} />
          Esci
        </button>
      </div>

      <div className="px-6 py-4 border-t border-stone-800">
        <div className="text-[11px] text-stone-500 uppercase tracking-widest mb-1">Settore</div>
        <div className="text-sm text-stone-300 leading-snug">
          Pubblica Illuminazione &<br />Giochi per Parco
        </div>
      </div>
    </aside>
  );
}
